import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircleDollarSign, ListOrdered } from "lucide-react";

import { api } from "@/api/client";
import type { CostQueryResult, CostTimeseriesResult, Pool, Service } from "@/api/types";
import { MetricTile } from "@/components/MetricTile";
import { Section } from "@/components/Section";

interface Props {
  service: Service;
  pools: Pool[];
}

const RANGES: { label: string; buckets: number; bucketSeconds: number }[] = [
  { label: "24h × 1h", buckets: 24, bucketSeconds: 3600 },
  { label: "7d × 1h", buckets: 168, bucketSeconds: 3600 },
  { label: "30d × 1d", buckets: 30, bucketSeconds: 86_400 },
];

export function ServiceCost({ service, pools }: Props) {
  const [rangeIdx, setRangeIdx] = useState(0);
  const range = RANGES[rangeIdx];

  const ts = useQuery({
    queryKey: ["cost-ts", "service", service.id, range.buckets, range.bucketSeconds],
    queryFn: () =>
      api.get<CostTimeseriesResult>(
        `/cost/timeseries?service_id=${service.id}&buckets=${range.buckets}&bucket_seconds=${range.bucketSeconds}`,
      ),
  });
  const grouped = useQuery({
    queryKey: ["cost-grouped", "service", service.id, range.buckets * range.bucketSeconds],
    queryFn: () => {
      const seconds = range.buckets * range.bucketSeconds;
      const to = new Date().toISOString();
      const from = new Date(Date.now() - seconds * 1000).toISOString();
      return api.get<CostQueryResult>(
        `/cost?group_by=pool&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );
    },
  });

  const points = useMemo(
    () =>
      (ts.data?.points ?? []).map((p) => ({
        ts: new Date(p.ts).toLocaleString(),
        usd: p.cost_usd,
      })),
    [ts.data],
  );

  const onlyServicePools = useMemo(() => {
    const allowed = new Set(pools.map((p) => p.sky_service_name));
    return (grouped.data?.buckets ?? []).filter((b) => allowed.has(b.group_key));
  }, [grouped.data, pools]);
  const total = onlyServicePools.reduce((acc, b) => acc + b.cost_usd, 0);

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <select
          value={rangeIdx}
          onChange={(e) => setRangeIdx(Number(e.target.value))}
        >
          {RANGES.map((r, i) => (
            <option key={r.label} value={i}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="metric-grid">
        <MetricTile
          icon={<CircleDollarSign />}
          label={`Spend · ${range.label}`}
          value={`$${(ts.data?.total_usd ?? 0).toFixed(2)}`}
          tone={(ts.data?.total_usd ?? 0) > 0 ? "brand" : "neutral"}
        />
        <MetricTile
          icon={<ListOrdered />}
          label="Pools with spend"
          value={onlyServicePools.length}
          unit={`of ${pools.length}`}
        />
      </div>

      <Section title="Spend trend" icon={<CircleDollarSign />}>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={points}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--edge)" />
            <XAxis
              dataKey="ts"
              stroke="var(--ink-muted)"
              tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }}
              minTickGap={32}
            />
            <YAxis
              stroke="var(--ink-muted)"
              tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
            />
            <Tooltip
              contentStyle={{
                background: "var(--surface)",
                border: "1px solid var(--edge-strong)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: "var(--ink-muted)" }}
            />
            <Line
              type="monotone"
              dataKey="usd"
              name="USD"
              stroke="var(--brand)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Section>

      <Section
        title="Per-pool cost"
        icon={<ListOrdered />}
        count={`${onlyServicePools.length}`}
        flush
      >
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Pool</th>
                <th style={{ textAlign: "right" }}>USD</th>
                <th style={{ textAlign: "right" }}>Replica seconds</th>
                <th style={{ textAlign: "right" }}>%</th>
              </tr>
            </thead>
            <tbody>
              {onlyServicePools.map((b) => (
                <tr key={b.group_key} className="hoverable">
                  <td className="mono">{b.group_key}</td>
                  <td className="num">{b.cost_usd.toFixed(4)}</td>
                  <td className="num muted">{b.replica_seconds.toFixed(0)}</td>
                  <td className="num muted">
                    {total > 0
                      ? ((b.cost_usd / total) * 100).toFixed(1) + "%"
                      : "—"}
                  </td>
                </tr>
              ))}
              {!onlyServicePools.length && (
                <tr>
                  <td colSpan={4} className="empty">
                    no spend in this window
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}
